/** Guide ids: the layout leg (dashboard → upload → file card) and the workbench leg. */
export const WORKFLOW_JOURNEY_GUIDE_ID = 'workflow.journey';
export const WORKFLOW_REMEDIATION_GUIDE_ID = 'workflow.remediation';

export type WorkflowFileId = string | number;

/** Which file the user chose to work on. */
export type WorkflowPath = 'new' | 'sample';

/**
 * How far the workflow got — what "resume" continues from:
 *  - `upload`       nothing uploaded yet (a resume would only restart);
 *  - `card`         a file is uploaded, the user has to open it from the dashboard;
 *  - `remediation`  the file was opened, the workbench leg is next / running.
 */
export type WorkflowStage = 'upload' | 'card' | 'remediation';

export interface WorkflowState {
  stage: WorkflowStage;
  path: WorkflowPath | null;
  /** The uploaded file; null until the dashboard list (or the remediation route) reveals its id. */
  fileId: WorkflowFileId | null;
  fileName: string;
  /** Epoch ms of the last change (a stale state is not offered for resume). */
  at: number;
}

/** Where the upload screen sent the user once the batch settled. */
export type WorkflowUploadOutcome =
  | { kind: 'dashboard'; fileIds: WorkflowFileId[] }
  | { kind: 'remediation'; fileId: WorkflowFileId }
  | { kind: 'orders' }
  | { kind: 'failed' };

/** Live facts of the upload screen, read by the guide on every poll. */
export interface WorkflowUploadProbe {
  fileCount: () => number;
  uploading: () => boolean;
  /** The onboarding walkthrough files are offered on this plan. */
  samplesAvailable: () => boolean;
  /** Name of the first queued file. */
  firstFileName: () => string;
}

/** The subset of a dashboard row the workflow reads (see `TaskList`). */
export interface WorkflowDashboardFile {
  id: WorkflowFileId;
  original_filename?: string;
  filename?: string;
  processed?: boolean;
  status?: string;
  process_state?: string;
}

/** Live facts of the dashboard screen. */
export interface WorkflowDashboardProbe {
  files: () => WorkflowDashboardFile[];
}

/** The tracked file as the dashboard list shows it. */
export type WorkflowFileStatus = 'unknown' | 'missing' | 'processing' | 'ready' | 'failed';
